import { API_BASE, refreshAccessToken, session } from '../api/client';
import { AppError } from '../api/errors';

export interface DownloadResult {
  filename: string;
  truncated: boolean;
}

/**
 * Descarcă un fișier de la API cu `Authorization` (token-ul e doar în memorie, un link direct nu l-ar avea).
 * La 401 încearcă o dată reîmprospătarea sesiunii. Numele fișierului vine din `Content-Disposition`.
 */
export async function downloadCsv(
  path: string,
  params: Record<string, string | undefined> = {},
): Promise<DownloadResult> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') qs.set(k, v);
  const url = `${API_BASE}${path}${qs.size ? `?${qs.toString()}` : ''}`;
  const doFetch = () => {
    const headers: Record<string, string> = {};
    if (session.accessToken) headers.Authorization = `Bearer ${session.accessToken}`;
    return fetch(url, { headers, credentials: 'same-origin' });
  };
  let res: Response;
  try {
    res = await doFetch();
    if (res.status === 401 && (await refreshAccessToken())) res = await doFetch();
  } catch (e) {
    throw AppError.network(e);
  }
  if (!res.ok) throw AppError.fromResponse(res.status, await res.json().catch(() => null));
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'export.csv';
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
  return { filename, truncated: res.headers.get('x-export-truncated') === 'true' };
}
